// BOGO Utilities - Shared functions for all BOGO components
window.BOGOUtils = {
  // State tracking to prevent jitter
  cachedBogoState: null,
  updateDebounceTimer: null,
  buttonTextObserver: null,

  // Check if item is excluded from BOGO
  isExcludedItem: function(item) {
    const excludedTerms = ['warranty', 'gift box', 'rose gift', 'engraving', 'birthstone'];
    const title = (item.title || '').toLowerCase();
    const productType = (item.product_type || '').toLowerCase();

    return excludedTerms.some(term =>
      title.includes(term) || productType.includes(term)
    );
  },

  // Get count of eligible items in cart
  getEligibleItemCount: function(cart) {
    let count = 0;
    if (cart && cart.items) {
      cart.items.forEach(item => {
        if (!this.isExcludedItem(item)) {
          count += item.quantity;
        }
      });
    }
    return count;
  },

  // Check if cart has BOGO discount applied
  hasBOGODiscount: function(cart) {
    if (!cart || !cart.cart_level_discount_applications) return false;

    return cart.cart_level_discount_applications.some(discount =>
      discount.title.toLowerCase().includes('buy one get one') ||
      discount.title.toLowerCase().includes('bogo')
    );
  },

  // Update progress bar based on eligible items
  updateProgressBar: function(eligibleCount) {
    const progressBar = document.querySelector('[data-bogo-progress-bar]');
    if (!progressBar) return;

    // Remove all active classes
    progressBar.querySelectorAll('.bogo-progress-step').forEach(step => {
      step.classList.remove('bogo-step--active', 'bogo-step--completed');
    });

    // Get step elements
    const step1 = progressBar.querySelector('[data-step="1"]');
    const step2 = progressBar.querySelector('[data-step="2"]');
    const step3 = progressBar.querySelector('[data-step="3"]');

    // Set active step based on count
    if (eligibleCount === 0) {
      if (step1) step1.classList.add('bogo-step--active');
    } else if (eligibleCount === 1) {
      if (step1) step1.classList.add('bogo-step--completed');
      if (step2) step2.classList.add('bogo-step--active');
    } else {
      if (step1) step1.classList.add('bogo-step--completed');
      if (step2) step2.classList.add('bogo-step--completed');
      if (step3) step3.classList.add('bogo-step--active');
    }

    // Update progress line
    const progressLine = progressBar.querySelector('.bogo-progress-line-fill');
    if (progressLine) {
      const progressPercentage = eligibleCount === 0 ? 0 : eligibleCount === 1 ? 50 : 100;
      progressLine.style.width = progressPercentage + '%';
    }
  },

  // Initialize BOGO progress bar
  initProgressBar: function() {
    // Get current cart and update progress bar
    if (window.theme && theme.cart && theme.cart.getCart) {
      theme.cart.getCart().then(cart => {
        const eligibleCount = this.getEligibleItemCount(cart);
        this.updateProgressBar(eligibleCount);
      });
    }

    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      const cart = event.detail ? event.detail.cart : null;
      if (cart) {
        const eligibleCount = this.getEligibleItemCount(cart);
        this.updateProgressBar(eligibleCount);
      }
    });

    // Also listen for theme cart updates
    document.addEventListener('cart:refresh', () => {
      if (window.theme && theme.cart && theme.cart.getCart) {
        theme.cart.getCart().then(cart => {
          const eligibleCount = this.getEligibleItemCount(cart);
          this.updateProgressBar(eligibleCount);
        });
      }
    });
  },

  // Update product page button text
  updateProductButton: function(cart, skipCache = false) {
    try {
      const eligibleCount = this.getEligibleItemCount(cart);
      const hasDiscount = this.hasBOGODiscount(cart);
      const currentBogoState = eligibleCount === 1 && !hasDiscount;

      // Check if state actually changed (prevent jitter)
      if (!skipCache && this.cachedBogoState === currentBogoState) {
        return; // No change needed, prevent DOM updates
      }

      // Update cache
      this.cachedBogoState = currentBogoState;

      // Find all add-to-cart buttons
      const addButtons = document.querySelectorAll('[data-add-to-cart]');

      addButtons.forEach(button => {
        try {
          const textElement = button.querySelector('[data-add-to-cart-text]');
          if (!textElement) return;

          // Store original text if not already stored
          if (!button.dataset.originalText) {
            button.dataset.originalText = textElement.textContent.trim();
          }

          // Check if button is disabled (for engraving products)
          if (button.disabled || button.hasAttribute('disabled')) {
            // Store BOGO state for when button becomes enabled
            button.dataset.pendingBogoUpdate = currentBogoState ? 'true' : 'false';
            return; // Skip updating disabled buttons
          }

          // Update text based on BOGO state
          if (currentBogoState) {
            // Use emoji directly in the text for consistency
            textElement.textContent = '\ud83c\udf81 ADD ITEM FOR FREE';
            button.classList.add('bogo-available');
          } else {
            // Safely restore original text
            const originalText = button.dataset.originalText || 'Add to Cart';
            textElement.textContent = originalText;
            button.classList.remove('bogo-available');
          }
        } catch (buttonError) {
          console.error('[BOGO] Error updating individual button:', buttonError, button);
          // Ensure button remains functional even if update fails
          if (button.classList.contains('bogo-available')) {
            button.classList.remove('bogo-available');
          }
        }
      });
    } catch (error) {
      console.error('[BOGO] Error in updateProductButton:', error);
      // Fail gracefully - ensure all buttons remain functional
      document.querySelectorAll('.bogo-available').forEach(button => {
        button.classList.remove('bogo-available');
      });
    }
  },

  // Debounced button update to prevent jitter
  debouncedButtonUpdate: function(cart) {
    // Clear existing timer
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }

    // Set new timer for 200ms to batch rapid updates
    this.updateDebounceTimer = setTimeout(() => {
      this.updateProductButton(cart);
    }, 200);
  },

  // Watch for disabled state changes on buttons (for engraving products)
  observeButtonStates: function() {
    try {
      // Disconnect existing observer if any
      if (this.buttonObserver) {
        this.buttonObserver.disconnect();
      }

      // Create observer to watch for disabled attribute changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
            const button = mutation.target;
            const textElement = button.querySelector('[data-add-to-cart-text]');

            if (!textElement) return;

            // When button becomes enabled, check if it needs BOGO update
            if (!button.disabled && !button.hasAttribute('disabled')) {
              if (button.dataset.pendingBogoUpdate === 'true') {
                // Apply BOGO text
                textElement.textContent = '\ud83c\udf81 ADD ITEM FOR FREE';
                button.classList.add('bogo-available');
              } else if (button.dataset.pendingBogoUpdate === 'false') {
                // Restore original text
                const originalText = button.dataset.originalText || 'Add to Cart';
                textElement.textContent = originalText;
                button.classList.remove('bogo-available');
              }
              // Clear the pending update flag
              delete button.dataset.pendingBogoUpdate;
            }
          }
        });
      });

      // Function to observe a button
      const observeButton = (button) => {
        if (!button.dataset.bogoObserved) {
          observer.observe(button, {
            attributes: true,
            attributeFilter: ['disabled']
          });
          button.dataset.bogoObserved = 'true';
        }
      };

      // Start observing all current add-to-cart buttons
      document.querySelectorAll('[data-add-to-cart]').forEach(observeButton);

      // Watch for new buttons added to the DOM
      const bodyObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              // Check if the node itself is an add-to-cart button
              if (node.matches && node.matches('[data-add-to-cart]')) {
                observeButton(node);
              }
              // Check for add-to-cart buttons within the added node
              if (node.querySelectorAll) {
                node.querySelectorAll('[data-add-to-cart]').forEach(observeButton);
              }
            }
          });
        });
      });

      bodyObserver.observe(document.body, {
        childList: true,
        subtree: true
      });

      // Store observer references for cleanup if needed
      this.buttonObserver = observer;
      this.bodyObserver = bodyObserver;

    } catch (error) {
      console.error('[BOGO] Error setting up button state observer:', error);
    }
  },

  // Watch for button text changes and instantly maintain BOGO text
  observeButtonTextChanges: function() {
    const self = this;

    // Create observer that watches for text changes
    const textObserver = new MutationObserver(function(mutations) {
      // Only act if BOGO should be active
      if (self.cachedBogoState !== true) return;

      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList' || mutation.type === 'characterData') {
          const button = mutation.target.closest('[data-add-to-cart]');
          if (!button || button.disabled) return;

          const textElement = button.querySelector('[data-add-to-cart-text]');
          if (textElement && textElement.textContent !== '\ud83c\udf81 ADD ITEM FOR FREE') {
            // Instantly reapply BOGO text without any delay
            textElement.textContent = '\ud83c\udf81 ADD ITEM FOR FREE';
            button.classList.add('bogo-available');
          }
        }
      });
    });

    // Observe all button text elements
    document.querySelectorAll('[data-add-to-cart-text]').forEach(textElement => {
      textObserver.observe(textElement, {
        childList: true,
        characterData: true,
        subtree: true
      });
    });

    this.buttonTextObserver = textObserver;
  },

  // Initialize button text updates
  initButtonText: function() {
    // Get current cart and update buttons (initial load, no debounce)
    if (window.theme && theme.cart && theme.cart.getCart) {
      theme.cart.getCart().then(cart => {
        this.updateProductButton(cart, true); // Skip cache check on initial load
      });
    }

    // Listen for cart updates - use debounced update
    document.addEventListener('cart:updated', (event) => {
      const cart = event.detail ? event.detail.cart : null;
      if (cart) {
        this.debouncedButtonUpdate(cart);
      }
    });

    // Listen for variant changes - use debounced update to prevent jitter
    document.addEventListener('variant:change', () => {
      // Only fetch cart if we don't have cached state
      // This prevents unnecessary flicker during variant changes
      if (window.theme && theme.cart && theme.cart.getCart) {
        theme.cart.getCart().then(cart => {
          this.debouncedButtonUpdate(cart);
        });
      }
    });
  },

  // Check if BOGO modal should be shown
  shouldShowBogoModal: function(cart) {
    // Check if modal is enabled in settings
    const modalEnabled = window.theme && theme.bogo && theme.bogo.modalEnabled !== false;
    if (!modalEnabled) return false;

    const eligibleCount = this.getEligibleItemCount(cart);
    const hasDiscount = this.hasBOGODiscount(cart);
    const modalShown = sessionStorage.getItem('bogoModalShown');


    return eligibleCount === 1 && !hasDiscount && !modalShown;
  },

  // Show BOGO modal in cart drawer
  showBogoModal: function() {
    const modal = document.querySelector('[data-bogo-modal]');
    if (modal) {
      modal.classList.add('bogo-modal--visible');
      sessionStorage.setItem('bogoModalShown', 'true');
    } else {
      console.error('[BOGO] Modal element not found!');
    }
  },

  // Hide BOGO modal
  hideBogoModal: function() {
    const modal = document.querySelector('[data-bogo-modal]');
    if (modal) {
      modal.classList.remove('bogo-modal--visible');
    }
  },

  // Initialize BOGO modal
  initBogoModal: function() {
    // Listen for cart drawer open
    document.addEventListener('cart:open', () => {
      if (window.theme && theme.cart && theme.cart.getCart) {
        setTimeout(() => {
          theme.cart.getCart().then(cart => {
            if (this.shouldShowBogoModal(cart)) {
              this.showBogoModal();
            }
          });
        }, 300); // Delay for smooth drawer animation
      }
    });

    // Also listen for drawer open class changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target.classList && mutation.target.classList.contains('drawer--is-open')) {
          if (window.theme && theme.cart && theme.cart.getCart) {
            setTimeout(() => {
              theme.cart.getCart().then(cart => {
                if (this.shouldShowBogoModal(cart)) {
                  this.showBogoModal();
                }
              });
            }, 300);
          }
        }
      });
    });

    // Observe the cart drawer element for class changes
    const cartDrawer = document.querySelector('.drawer--right');
    if (cartDrawer) {
      observer.observe(cartDrawer, { attributes: true, attributeFilter: ['class'] });
    }

    // Close button handler
    document.addEventListener('click', (e) => {
      if (e.target.closest('.bogo-modal-close')) {
        e.stopPropagation();  // Prevent event from bubbling up to cart drawer
        e.preventDefault();   // Prevent any default button behavior
        this.hideBogoModal();
      }
      // Close on outside click within drawer
      if (e.target.classList.contains('bogo-modal-overlay')) {
        e.stopPropagation();  // Prevent event from bubbling up
        this.hideBogoModal();
      }
    });
  }
};

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', function() {
  // Inject CSS for smooth transitions
  const style = document.createElement('style');
  style.textContent = `
    [data-add-to-cart] [data-add-to-cart-text] {
      transition: opacity 0.2s ease;
    }
    [data-add-to-cart].bogo-available [data-add-to-cart-text] {
      transition: opacity 0.2s ease;
    }
  `;
  document.head.appendChild(style);

  BOGOUtils.initProgressBar();
  BOGOUtils.initButtonText();
  BOGOUtils.observeButtonStates();
  BOGOUtils.observeButtonTextChanges();
  BOGOUtils.initBogoModal();
});

// Also initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', function() {
  BOGOUtils.initProgressBar();
  BOGOUtils.initButtonText();
  BOGOUtils.observeButtonStates();
});